import MultiRankingStatCalculator from "../MultiRankingStatCalculator.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import { RankingStat } from "../../../entities/index.js";
import { Out, OutType, PitchCall, PitchData, BattingRankingStats, PitchingRankingStats, DataSection, ResultType, ResultDataNew, ScoringData, TaggingData, RankingScopeContext } from "../../../enums/index.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import classifiesAsAirBall from './../../../classificators/battedBallType/classifiesAsAirBall.js';
import classifiesAsGroundBall from './../../../classificators/battedBallType/classifiesAsGroundBall.js';
import { anyLeague } from "../../../classificators/league/index.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import resultsSliceBeforePitchType from "../../../classificators/resultsSlice/resultsSliceBeforePitchType.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import IterableDataProcessor from './../../../entities/IterableDataProcessor.js';
import EnumValueProvider from './../../../providers/EnumValueProvider.js';
import classifiesAsBatterOut from './../../../classificators/plateAppearanceResult/classifiesAsBatterOut.js';
import classifiesAsFieldersChoice from './../../../classificators/plateAppearanceResult/classifiesAsFieldersChoice.js';

class NewOutTypeCalculator extends MultiRankingStatCalculator {
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache,
        groundBallClassifier, airBallClassifier, batterOutClassifier, fieldersChoiceClassifier
    ) {
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }
        if (typeof(groundBallClassifier) !== 'undefined' && typeof(groundBallClassifier) !== 'function') {
            throw new TypeError('groundBallClassifier must be an function or undefined');
        }
        if (typeof(airBallClassifier) !== 'undefined' && typeof(airBallClassifier) !== 'function') {
            throw new TypeError('airBallClassifier must be an function or undefined');
        }
        if (typeof(batterOutClassifier) !== 'undefined' && typeof(batterOutClassifier) !== 'function') {
            throw new TypeError('batterOutClassifier must be an function or undefined');
        }
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }

        super(
            context === RankingScopeContext.Pitching.key
                ? [ PitchingRankingStats.GroundOuts, PitchingRankingStats.AirOuts, PitchingRankingStats.GroundOutToAirOutRatio ]
                : [ BattingRankingStats.GroundOuts, BattingRankingStats.AirOuts, BattingRankingStats.GroundOutToAirOutRatio ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key
                        ? pitchingScopeContext
                        : battingScopeContext,
                    resultsSliceBeforePitchType
                )
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.context = context;
        this.groundBallClassifier = groundBallClassifier || classifiesAsGroundBall;
        this.airBallClassifier = airBallClassifier || classifiesAsAirBall;
        this.batterOutClassifier = batterOutClassifier || classifiesAsBatterOut;
        this.fieldersChoiceClassifier = fieldersChoiceClassifier || classifiesAsFieldersChoice;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        const results = slice.getSmallestSlice();

        let groundOuts = 0;
        let airOuts = 0;
        
        for (let r = 0; r < results.length; r++) {
            const result = results[r];
            
            if (
                result && result[ResultDataNew.Data.key]
                && result[ResultDataNew.Data.key][DataSection.Tagging.key]
            ) {
                if (!result[ResultDataNew.Data.key][DataSection.Tagging.key].pitch) {
                    continue;
                }
                if (!result[ResultDataNew.Data.key][DataSection.Scoring.key]) {
                    continue;
                }

                // @see https://www.mlb.com/glossary/standard-stats/groundout
                // @see https://en.wikipedia.org/wiki/Ground_out_(baseball)
                // @see https://www.sportspundit.com/baseball/terms/1927-air-out
                const pitchCall = this.resultsProcessor.getObjectContent(
                    result, ResultDataNew.getDataPath(ScoringData.PitchCall.key, DataSection.Scoring.key)
                );
                // A groundout occurs when a batter hits a ball on the ground to a fielder, who records an out by throwing to or stepping on first base. It can also occur when the batter reaches first base -- and the defense instead opts to record an out elsewhere via a "fielder's choice."
                // AND - Air Out or AO is a type of out that is recorded when the ball is caught by a defensive player before it touches the ground. An umpire can rule an Air Out due to a sacrifice fly, an infield fly, a lineout, or a caught fly ball.
                // if it wasn't a hit, we can easily skip it...
                if (pitchCall !== PitchCall.BallInPlay.key) {
                    continue;
                }

                // @see https://www.mlb.com/glossary/standard-stats/groundout-to-airout-ratio
                // ...Groundout-to-airout ratio is obtained by dividing the total number of ground balls converted into outs (not including bunts)
                const isBunt = this.resultsProcessor.getObjectContent(
                    result, ResultDataNew.getDataPath(TaggingData.IsBunt.key, DataSection.Tagging.key, ResultType.Pitch.key)
                );
                if (typeof(isBunt) === 'undefined' || Number.parseInt(isBunt, 10) === 1) {
                    continue;
                }

                // check what happend with the batter, he needs to be out or advance on fielder's choice (so the out has been made elsewhere)
                // ... It can also occur when the batter reaches first base -- and the defense instead opts to record an out elsewhere via a "fielder's choice."
                const paResult = this.resultsProcessor.getObjectContent(
                    result, ResultDataNew.getDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
                );
                if (!this.batterOutClassifier(paResult)) {
                    if (!this.fieldersChoiceClassifier(paResult)) {
                        continue;
                    }
                    // now let's exclude unsuccessfull fielder's choice, where no out is made
                    const outs =  this.resultsProcessor.getObjectContent(
                        result, ResultDataNew.getDataPath(ScoringData.Outs.key, DataSection.Scoring.key)
                    );
                    if (!outs || !outs.length || !outs.some(o => o[Out.Type.key] === OutType.OnLastPlay.key)) {
                        continue;
                    }
                }

                const battedBallType = this.resultsProcessor.getObjectContent(
                    result, ResultDataNew.getDataPath(TaggingData.BattedBallType.key, DataSection.Tagging.key, ResultType.Pitch.key)
                );

                if (this.groundBallClassifier(battedBallType)){
                    groundOuts++;
                }
                if (this.airBallClassifier(battedBallType)){
                    airOuts++;
                }
            }
        }

        const output = {};

        // now include stats
        if (groundOuts > 0) {
            const groundOutsParam = this.supportedParams[0];
            output[groundOutsParam.key] = RankingStat.fromParameter(
                groundOutsParam, this.numberFormatter.format(groundOuts), groundOuts
            );
        }

        if (airOuts > 0) {
            const airOutsParam = this.supportedParams[1];
            output[airOutsParam.key] = RankingStat.fromParameter(
                airOutsParam, this.numberFormatter.format(airOuts), airOuts
            );

            const goToAoParam = this.supportedParams[2];
            output[goToAoParam.key] = RankingStat.fromParameter(
                goToAoParam, this.numberFormatter.format(groundOuts / airOuts), airOuts
            );
        }

        return output;
    }
}

export default NewOutTypeCalculator;